import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface VideoItem {
  id: string;
  filename: string;
  size: number;
  uploadDate: string;
  url: string;
  tags: string[];
  description: string;
  group: string;
}

const GroupPage: React.FC = () => {
  const [groups, setGroups] = useState<{ name: string; count: number }[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const fetchGroups = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/videos');
      if (response.ok) {
        const videos: VideoItem[] = await response.json();
        
        // 统计分组
        const groupMap = new Map<string, number>();
        videos.forEach(video => {
          const groupName = video.group || '默认分组';
          groupMap.set(groupName, (groupMap.get(groupName) || 0) + 1);
        });
        
        const groupList = Array.from(groupMap.entries()).map(([name, count]) => ({
          name,
          count
        }));
        
        setGroups(groupList);
        setError(null);
      } else {
        setError('获取分组列表失败');
      }
    } catch (err) {
      setError('网络错误');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  const handleGroupClick = (groupName: string) => {
    // 跳转到搜索页面并按分组筛选
    navigate(`/search?group=${encodeURIComponent(groupName)}`);
  };

  return (
    <div className="min-h-screen bg-black text-white p-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold mb-6 text-center">📁 视频分组</h1>
        
        {isLoading ? (
          <div className="text-center py-12">
            <div className="text-white text-lg">加载分组中...</div>
          </div>
        ) : error ? (
          <div className="text-center py-12 text-red-400">
            <div className="text-4xl mb-4">⚠️</div>
            <p>{error}</p>
            <button 
              onClick={fetchGroups}
              className="mt-4 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded"
            >
              重试
            </button>
          </div>
        ) : groups.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <div className="text-4xl mb-4">📁</div>
            <p>暂无视频分组</p>
            <a 
              href="/admin" 
              className="mt-4 inline-block bg-green-600 hover:bg-green-700 px-4 py-2 rounded"
            >
              去管理后台添加分组
            </a>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {groups.map((group, index) => (
              <div 
                key={index}
                className="bg-gray-900 rounded-lg p-4 cursor-pointer hover:bg-gray-800 transition-colors"
                onClick={() => handleGroupClick(group.name)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-lg">{group.name}</div>
                    <div className="text-sm text-gray-400">{group.count} 个视频</div>
                  </div>
                  <div className="text-2xl">📁</div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 text-center">
          <button 
            onClick={() => navigate('/')}
            className="inline-block bg-green-600 hover:bg-green-700 px-6 py-3 rounded-lg font-medium transition-colors"
          >
            🎬 返回视频播放器
          </button>
        </div>
      </div>
    </div>
  );
};

export default GroupPage;